import React, { useEffect, useRef, useState } from 'react';
import { blePrintKOT, bleClearLastPrinter } from '../../utils/blePrinter';

const KOTPrint = ({ order, restaurant, billingSettings, onClose }) => {
  const kotRef = useRef(null);
  const [bleStatus, setBleStatus] = useState('idle');
  const [bleError, setBleError] = useState('');

  const paperWidth = billingSettings?.thermal_paper_width || 80;
  const sheetWidth = paperWidth - 4;

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'menuron-kot-print-style';
    style.textContent = `
      @media print {
        @page { size: ${paperWidth}mm auto; margin: 0; }
        body > * { visibility: hidden !important; }
        #menuron-kot-overlay {
          visibility: visible !important;
          position: fixed !important;
          inset: 0 !important;
          background: white !important;
          padding: 0 !important;
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
        }
        #menuron-kot-overlay * { visibility: visible !important; }
        #menuron-kot-no-print { display: none !important; }
        #menuron-kot-sheet {
          width: ${sheetWidth}mm !important;
          max-width: ${sheetWidth}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          line-height: 1.4 !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('menuron-kot-print-style');
      if (el) el.remove();
    };
  }, [paperWidth, sheetWidth]);

  const handleBlePrint = async () => {
    setBleStatus('connecting');
    setBleError('');
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    try {
      await blePrintKOT({ order, restaurant, billingSettings });
      setBleStatus('done');
      setTimeout(() => setBleStatus('idle'), 3000);
    } catch (e) {
      if ((e.name === 'NotFoundError' || e.message?.includes('cancelled')) && !window.menuronDesktop?.isDesktop) {
        setBleStatus('idle');
      } else if (e.message?.includes('timed out')) {
        setBleStatus('offline');
      } else {
        setBleError(e.message || e.name || 'Print failed');
        setBleStatus('error');
        setTimeout(() => setBleStatus('idle'), 8000);
      }
    }
  };

  const handleChangePrinter = () => {
    bleClearLastPrinter();
    setBleStatus('idle');
  };

  const dateObj = new Date(order.created_at);
  const dateStr = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const tableName = order.restaurant_tables
    ? (order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`)
    : null;

  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 2000, padding: '20px', overflowY: 'auto',
  };
  const sheet = {
    background: 'white', width: '420px', maxWidth: '100%',
    borderRadius: '4px', fontFamily: "'Courier New', Courier, monospace",
    fontSize: '13px', color: '#111', lineHeight: '1.5',
    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
  };
  const section = { padding: '14px 18px', borderBottom: '1px dashed #ccc' };
  const metaRow = { display: 'flex', gap: '6px', padding: '2px 0', fontSize: '12px', color: '#333' };

  return (
    <div id="menuron-kot-overlay" style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      {/* Action bar */}
      <div id="menuron-kot-no-print" style={{
        position: 'fixed', top: '16px', right: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', gap: '8px', zIndex: 2100,
      }}>
        <button onClick={() => window.print()} style={{
          background: '#111', color: 'white', border: 'none', borderRadius: '8px',
          padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>
          Print
        </button>
        {bleStatus === 'offline' && (
          <div style={{ color: '#fbbf24', fontSize: '0.82rem', fontWeight: '600' }}>
            Printer offline — is it on?
          </div>
        )}
        <button
          onClick={handleBlePrint}
          disabled={bleStatus === 'connecting'}
          style={{
            background: bleStatus === 'done' ? '#16a34a' : bleStatus === 'error' || bleStatus === 'offline' ? '#dc2626' : '#7c3aed',
            color: 'white', border: 'none', borderRadius: '8px',
            padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem',
            cursor: bleStatus === 'connecting' ? 'not-allowed' : 'pointer',
            opacity: bleStatus === 'connecting' ? 0.7 : 1,
          }}
        >
          {bleStatus === 'connecting' ? 'Connecting...' :
           bleStatus === 'done'       ? 'Printed!' :
           bleStatus === 'offline' || bleStatus === 'error' ? 'Retry' :
           'Bluetooth Print'}
        </button>
        <button onClick={handleChangePrinter} style={{
          background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)',
          borderRadius: '8px', padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>Change Printer</button>
        {bleStatus === 'error' && bleError && (
          <div style={{ color: '#fca5a5', fontSize: '0.75rem', maxWidth: '180px' }}>
            {bleError}
          </div>
        )}
        <button onClick={onClose} style={{
          background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)',
          borderRadius: '8px', padding: '10px 18px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
        }}>Close</button>
      </div>

      {/* KOT sheet */}
      <div id="menuron-kot-sheet" ref={kotRef} style={{ ...sheet, marginTop: '60px', marginBottom: '20px' }}>

        {/* Header */}
        <div style={{ ...section, textAlign: 'center', borderBottom: '2px solid #111' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', color: '#555', marginBottom: '4px' }}>
            KITCHEN ORDER TICKET
          </div>
          <div style={{ fontSize: '17px', fontWeight: '800', letterSpacing: '2px', marginBottom: '6px' }}>KOT</div>
          <div style={{ fontSize: '15px', fontWeight: '700' }}>{restaurant?.name}</div>
        </div>

        {/* Meta */}
        <div style={section}>
          <div style={metaRow}>
            <span style={{ color: '#555', minWidth: '110px' }}>Order / KOT No</span>
            <span style={{ fontWeight: '700', fontSize: '15px' }}>#{order.order_number || '—'}</span>
          </div>
          <div style={metaRow}>
            <span style={{ color: '#555', minWidth: '110px' }}>Date & Time</span>
            <span>{dateStr}, {timeStr}</span>
          </div>
          <div style={metaRow}>
            <span style={{ color: '#555', minWidth: '110px' }}>Order Type</span>
            <span>{order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Dine-In'}</span>
          </div>
          {tableName && (
            <div style={metaRow}>
              <span style={{ color: '#555', minWidth: '110px' }}>Table</span>
              <span style={{ fontWeight: '700' }}>{tableName}</span>
            </div>
          )}
        </div>

        {/* Items — qty prominent, no prices */}
        <div style={section}>
          <div style={{
            display: 'flex', fontSize: '11px', fontWeight: '700', color: '#555',
            paddingBottom: '6px', borderBottom: '1px solid #ddd', marginBottom: '6px',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            <span style={{ flex: 1 }}>Item</span>
            <span style={{ width: '48px', textAlign: 'right', flexShrink: 0 }}>Qty</span>
          </div>
          {(order.order_items || []).map((oi, idx) => (
            <div key={oi.id || idx} style={{
              display: 'flex', padding: '5px 0',
              borderBottom: '1px dotted #eee', fontSize: '13px',
            }}>
              <span style={{ flex: 1, minWidth: 0, paddingRight: '8px', fontWeight: '600' }}>
                {oi.menu_items?.name || 'Item'}
              </span>
              <span style={{
                width: '48px', textAlign: 'right', flexShrink: 0,
                fontWeight: '800', fontSize: '16px', color: '#111',
              }}>{oi.quantity}</span>
            </div>
          ))}
        </div>

        {/* Notes */}
        {order.notes && (
          <div style={section}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Notes
            </div>
            <div style={{ fontSize: '12.5px', color: '#333', lineHeight: '1.5' }}>{order.notes}</div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '10px 18px', textAlign: 'center', color: '#aaa', fontSize: '10px' }}>
          System generated · KOT
        </div>
      </div>
    </div>
  );
};

export default KOTPrint;
